import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  CheckCircleIcon,
  ClipboardDocumentListIcon,
  Cog6ToothIcon,
  PlusIcon,
  StarIcon,
  UserIcon,
  ExclamationTriangleIcon,
} from "@heroicons/react/24/outline";
import {
  mergeTodoWorkspaceParams,
  parseTodoGroupBy,
  parseTodoMine,
  parseTodoTodayPlan,
  parseWorkspaceViewTab,
} from "./todoWorkspaceSearchParams";

/**
 * Fixed bottom navigation for todo workspace on mobile/tablet (< lg).
 */
export function TodoMobileNav() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const currentUser = useSelector((state) => state.session?.currentUser);
  const canManageSharedTasks =
    currentUser?.roles?.some((role) => role.name === "admin") || false;

  const showMine = parseTodoMine(searchParams);
  const showTodayPlan = parseTodoTodayPlan(searchParams);
  const groupBy = parseTodoGroupBy(searchParams);
  const workspaceTab = parseWorkspaceViewTab(searchParams);

  const patchParams = (patch) => {
    setSearchParams((prev) => mergeTodoWorkspaceParams(prev, patch), { replace: true });
  };

  const activeKey = useMemo(() => {
    if (workspaceTab === 1) return "journal";
    if (showTodayPlan) return "today";
    if (groupBy === "priority") return "priority";
    return "todo";
  }, [workspaceTab, showTodayPlan, groupBy]);

  const baseNavItems = useMemo(
    () => [
      {
        key: "todo",
        label: t("todo.mobileNav.todo"),
        icon: CheckCircleIcon,
        action: () =>
          patchParams({
            view: null,
            todayPlan: false,
            groupBy: "status",
            mine: false,
            status: "active",
          }),
      },
      {
        key: "today",
        label: t("todo.mobileNav.today"),
        icon: StarIcon,
        action: () =>
          patchParams({
            view: null,
            todayPlan: true,
            groupBy: "status",
            status: "active",
          }),
      },
      {
        key: "priority",
        label: t("todo.mobileNav.priority"),
        icon: ExclamationTriangleIcon,
        action: () =>
          patchParams({
            view: null,
            todayPlan: false,
            groupBy: "priority",
            status: "active",
          }),
      },
      {
        key: "newTask",
        label: t("todo.mobileNav.newTask"),
        icon: PlusIcon,
        action: () =>
          patchParams({
            view: null,
            create: true,
            todayPlan: false,
            groupBy: "status",
            status: "active",
          }),
      },
      {
        key: "mine",
        label: t("todo.mobileNav.mine"),
        icon: UserIcon,
        toggle: true,
        action: () => patchParams({ mine: !showMine }),
      },
      {
        key: "journal",
        label: t("todo.mobileNav.journal"),
        icon: ClipboardDocumentListIcon,
        action: () =>
          patchParams({
            view: "journal",
            todayPlan: false,
            groupBy: "status",
          }),
      },
    ],
    [navigate, patchParams, showMine, t]
  );

  const navItems = useMemo(() => {
    const items = [...baseNavItems];
    if (canManageSharedTasks) {
      items.push({
        key: "settings",
        label: t("todo.mobileNav.settings"),
        icon: Cog6ToothIcon,
        action: () => navigate("/dashboard/todos/settings"),
      });
    }
    return items;
  }, [baseNavItems, canManageSharedTasks, navigate, t]);

  return (
    <div
      className="td-mobile-nav-wrap lg:hidden pointer-events-none fixed bottom-0 left-0 right-0 z-50"
      style={{ paddingBottom: "max(0.35rem, env(safe-area-inset-bottom, 0px))" }}
    >
      <nav
        className="td-mobile-nav pointer-events-auto"
        aria-label={t("todo.mobileNav.ariaLabel")}
      >
        <div className={`td-mobile-nav-grid${canManageSharedTasks ? " is-admin" : ""}`}>
          {navItems.map((item) => {
            const Icon = item.icon;
            const active = item.toggle ? showMine : activeKey === item.key;
            return (
              <button
                key={item.key}
                type="button"
                className={`td-mobile-nav-item${active ? " is-active" : ""}`}
                aria-current={!item.toggle && active ? "page" : undefined}
                aria-pressed={item.toggle ? showMine : undefined}
                onClick={item.action}
                title={item.label}
              >
                <Icon className="td-mobile-nav-icon" aria-hidden />
                <span className="td-mobile-nav-label">{item.label}</span>
              </button>
            );
          })}
        </div>
      </nav>
    </div>
  );
}
